/**
 * Financial Dashboard Page (Admin)
 *
 * Shows platform revenue, margins, active resources, top customers,
 * buyer/seller breakdown, and config. Proxied via admin backend to
 * client dashboard's admin-finance API (real MeteringEvent data).
 */

import { useEffect, useState, useCallback } from 'react';
import {
  DollarSign,
  TrendingUp,
  Activity,
  Settings,
  RefreshCw,
  Loader2,
  BarChart3,
  Wallet,
  Users,
  ArrowRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/auth';

const GOD_MODE_ADMIN_EMAIL = 'admin@tyaus.com';

async function fetchFinance(path: string) {
  const res = await api.get(`/api/v1/admin/finance${path}`);
  return res.data;
}

export default function FinancialDashboardPage() {
  const { user } = useAuthStore();
  const isGodModeAdmin = (user?.email ?? '').toLowerCase() === GOD_MODE_ADMIN_EMAIL;

  const [overview, setOverview] = useState<any>(null);
  const [revenueHistory, setRevenueHistory] = useState<any[]>([]);
  const [parties, setParties] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [godTargetMode, setGodTargetMode] = useState<'email' | 'id'>('email');
  const [godEmail, setGodEmail] = useState('');
  const [godUserId, setGodUserId] = useState('');
  const [godAmount, setGodAmount] = useState('');
  const [godNote, setGodNote] = useState('');
  const [godSubmitting, setGodSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ov, rev, pt] = await Promise.all([
        fetchFinance('/overview'),
        fetchFinance('/revenue?months=12'),
        fetchFinance('/parties').catch(() => null),
      ]);
      setOverview(ov);
      setRevenueHistory(rev.revenue || []);
      setParties(pt);
    } catch (err: any) {
      setError(err.message || 'Failed to load financial data');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const submitGodCredit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isGodModeAdmin) return;

    const amountUsd = parseFloat(godAmount);
    if (!Number.isFinite(amountUsd) || amountUsd < 0.01) {
      toast.error('Enter a valid amount (minimum $0.01)');
      return;
    }

    const payload =
      godTargetMode === 'email'
        ? { targetEmail: godEmail.trim().toLowerCase(), amountUsd, note: godNote.trim() || undefined }
        : { targetUserId: godUserId.trim(), amountUsd, note: godNote.trim() || undefined };

    if (godTargetMode === 'email' && !godEmail.trim()) {
      toast.error('Enter the client user email');
      return;
    }
    if (godTargetMode === 'id' && !godUserId.trim()) {
      toast.error('Enter the client user ID');
      return;
    }

    setGodSubmitting(true);
    try {
      const res = await api.post('/api/v1/admin/finance/god-credit', payload);
      toast.success(res.data?.message || 'Balance updated');
      setGodAmount('');
      setGodNote('');
      await fetchData();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to grant credit';
      toast.error(typeof msg === 'string' ? msg : 'Failed to grant credit');
    } finally {
      setGodSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
          {error}
          <button onClick={fetchData} className="ml-4 underline">Retry</button>
        </div>
      </div>
    );
  }

  const rev = overview?.revenue;
  const feeRate = overview?.config?.MARKETPLACE_PLATFORM_FEE_PERCENT ?? '15';

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Financial Dashboard</h1>
          <p className="text-sm text-gray-400 mt-1">Platform revenue, margins, and resource overview</p>
        </div>
        <button onClick={fetchData} className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-300 border border-gray-600 rounded-lg hover:bg-gray-700 transition-colors self-start sm:self-auto">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {isGodModeAdmin && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-5">
          <h2 className="text-sm font-semibold text-amber-200 mb-1">God mode — client balance</h2>
          <p className="text-xs text-amber-200/70 mb-4">
            Add prepaid balance for a GPU Cloud client user with no Stripe charge. Only visible to {GOD_MODE_ADMIN_EMAIL}.
          </p>
          <form onSubmit={submitGodCredit} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-2 flex gap-2">
              <button
                type="button"
                onClick={() => setGodTargetMode('email')}
                className={`flex-1 text-xs py-2 rounded-lg border ${godTargetMode === 'email' ? 'border-amber-400 text-amber-100 bg-amber-500/10' : 'border-gray-600 text-gray-400'}`}
              >
                By email
              </button>
              <button
                type="button"
                onClick={() => setGodTargetMode('id')}
                className={`flex-1 text-xs py-2 rounded-lg border ${godTargetMode === 'id' ? 'border-amber-400 text-amber-100 bg-amber-500/10' : 'border-gray-600 text-gray-400'}`}
              >
                By user ID
              </button>
            </div>
            {godTargetMode === 'email' ? (
              <div className="lg:col-span-4">
                <label className="block text-xs text-gray-500 mb-1">Client email</label>
                <input
                  className="w-full rounded-lg bg-gray-900 border border-gray-600 px-3 py-2 text-sm text-white"
                  type="email"
                  autoComplete="off"
                  placeholder="client@example.com"
                  value={godEmail}
                  onChange={(e) => setGodEmail(e.target.value)}
                  disabled={godSubmitting}
                />
              </div>
            ) : (
              <div className="lg:col-span-4">
                <label className="block text-xs text-gray-500 mb-1">Client user ID (UUID)</label>
                <input
                  className="w-full rounded-lg bg-gray-900 border border-gray-600 px-3 py-2 text-xs text-white font-mono"
                  autoComplete="off"
                  placeholder="uuid"
                  value={godUserId}
                  onChange={(e) => setGodUserId(e.target.value)}
                  disabled={godSubmitting}
                />
              </div>
            )}
            <div className="lg:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Amount (USD)</label>
              <input
                className="w-full rounded-lg bg-gray-900 border border-gray-600 px-3 py-2 text-sm text-white font-mono"
                type="number"
                min={0.01}
                step="0.01"
                placeholder="0.00"
                value={godAmount}
                onChange={(e) => setGodAmount(e.target.value)}
                disabled={godSubmitting}
              />
            </div>
            <div className="lg:col-span-3">
              <label className="block text-xs text-gray-500 mb-1">Note (optional)</label>
              <input
                className="w-full rounded-lg bg-gray-900 border border-gray-600 px-3 py-2 text-sm text-white"
                type="text"
                placeholder="e.g. trial credit"
                value={godNote}
                onChange={(e) => setGodNote(e.target.value)}
                disabled={godSubmitting}
              />
            </div>
            <div className="lg:col-span-1">
              <button
                type="submit"
                disabled={godSubmitting}
                className="w-full py-2 rounded-lg text-sm font-medium bg-amber-600 hover:bg-amber-500 text-black disabled:opacity-50"
              >
                {godSubmitting ? '…' : 'Credit'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Revenue cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-5">
          <div className="flex items-center gap-2 text-gray-400 text-xs mb-2">
            <DollarSign className="w-4 h-4" />
            Today's Revenue
          </div>
          <p className="text-2xl font-bold text-white">${rev?.today?.gross?.toFixed(2) ?? '0.00'}</p>
          <p className="text-xs text-green-400 mt-1">Platform fee: ${rev?.today?.platformFee?.toFixed(2) ?? '0.00'}</p>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-5">
          <div className="flex items-center gap-2 text-gray-400 text-xs mb-2">
            <TrendingUp className="w-4 h-4" />
            This Month
          </div>
          <p className="text-2xl font-bold text-white">${rev?.thisMonth?.gross?.toFixed(2) ?? '0.00'}</p>
          <p className="text-xs text-green-400 mt-1">Platform: ${rev?.thisMonth?.platformFee?.toFixed(2) ?? '0.00'}</p>
          <p className="text-xs text-blue-400">Seller earnings: ${rev?.thisMonth?.sellerEarnings?.toFixed(2) ?? '0.00'}</p>
          <p className="text-xs text-gray-500">{rev?.thisMonth?.transactions ?? 0} transactions</p>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-5">
          <div className="flex items-center gap-2 text-gray-400 text-xs mb-2">
            <BarChart3 className="w-4 h-4" />
            All Time Revenue
          </div>
          <p className="text-2xl font-bold text-white">${rev?.allTime?.gross?.toFixed(2) ?? '0.00'}</p>
          <p className="text-xs text-green-400 mt-1">Platform: ${rev?.allTime?.platformFee?.toFixed(2) ?? '0.00'}</p>
          <p className="text-xs text-blue-400">Seller earnings: ${rev?.allTime?.sellerEarnings?.toFixed(2) ?? '0.00'}</p>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-5">
          <div className="flex items-center gap-2 text-gray-400 text-xs mb-2">
            <Activity className="w-4 h-4" />
            Active Resources
          </div>
          <p className="text-2xl font-bold text-white">{overview?.activeResources?.total ?? 0}</p>
          <p className="text-xs text-gray-400 mt-1">
            {overview?.activeResources?.deployments ?? 0} deployments · {overview?.activeResources?.rentals ?? 0} rentals
          </p>
        </div>
      </div>

      {/* Money Flow Breakdown */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-white mb-4">Money Flow (This Month)</h3>
        <div className="flex items-center justify-center gap-3 flex-wrap text-sm">
          <div className="text-center p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg min-w-[140px]">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide">Buyers Paid</p>
            <p className="text-lg font-bold text-blue-400">${rev?.thisMonth?.gross?.toFixed(2) ?? '0.00'}</p>
          </div>
          <ArrowRight className="w-4 h-4 text-gray-500 shrink-0" />
          <div className="text-center p-3 bg-green-500/10 border border-green-500/30 rounded-lg min-w-[140px]">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide">Platform Keeps ({feeRate}%)</p>
            <p className="text-lg font-bold text-green-400">${rev?.thisMonth?.platformFee?.toFixed(2) ?? '0.00'}</p>
          </div>
          <span className="text-gray-500 text-lg shrink-0">+</span>
          <div className="text-center p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg min-w-[140px]">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide">Sellers Earn ({100 - Number(feeRate)}%)</p>
            <p className="text-lg font-bold text-purple-400">${rev?.thisMonth?.sellerEarnings?.toFixed(2) ?? '0.00'}</p>
          </div>
        </div>
      </div>

      {/* Revenue by Type + Top Customers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-white mb-4">Revenue by Type (This Month)</h3>
          {(rev?.byType ?? []).length === 0 ? (
            <p className="text-sm text-gray-500">No revenue data yet</p>
          ) : (
            <div className="space-y-3">
              {rev.byType.map((t: any) => (
                <div key={t.type} className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white">{t.type === 'DEPLOYMENT' ? 'Deployments' : 'GPU Rentals'}</p>
                    <p className="text-xs text-gray-500">{t.count} events</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-white">${t.gross.toFixed(2)}</p>
                    <p className="text-xs text-green-400">Fee: ${t.platformFee.toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-white mb-4">Top Customers (This Month)</h3>
          {(overview?.topCustomers ?? []).length === 0 ? (
            <p className="text-sm text-gray-500">No customer data yet</p>
          ) : (
            <div className="space-y-2">
              {overview.topCustomers.slice(0, 8).map((c: any, i: number) => (
                <div key={c.userId} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600 w-4">{i + 1}.</span>
                    <span className="text-white">{c.name || c.email}</span>
                  </div>
                  <span className="font-mono text-green-400">${c.spend.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Buyers & Sellers Report */}
      {parties && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Buyers */}
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-4 h-4 text-blue-400" />
              <h3 className="text-sm font-semibold text-white">GPU Buyers</h3>
              <span className="text-xs text-gray-500 ml-auto">{parties.buyers?.length ?? 0} buyers</span>
            </div>
            {(parties.buyers ?? []).length === 0 ? (
              <p className="text-sm text-gray-500">No buyer data yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500 text-left">
                      <th className="pb-2 pr-3">Buyer</th>
                      <th className="pb-2 pr-3 text-right">This Month</th>
                      <th className="pb-2 pr-3 text-right">All-Time</th>
                      <th className="pb-2 text-right">Rentals</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    {parties.buyers.slice(0, 15).map((b: any) => (
                      <tr key={b.userId} className="border-t border-gray-700/50">
                        <td className="py-1.5 pr-3">
                          <p className="text-white">{b.name}</p>
                          <p className="text-gray-500 truncate max-w-[160px]">{b.email}</p>
                        </td>
                        <td className="py-1.5 pr-3 font-mono text-right text-blue-400">${b.thisMonthSpend.toFixed(2)}</td>
                        <td className="py-1.5 pr-3 font-mono text-right">${b.totalSpend.toFixed(2)}</td>
                        <td className="py-1.5 font-mono text-right text-gray-400">{b.rentalCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Sellers */}
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Wallet className="w-4 h-4 text-purple-400" />
              <h3 className="text-sm font-semibold text-white">GPU Sellers</h3>
              <span className="text-xs text-gray-500 ml-auto">{parties.sellers?.length ?? 0} sellers</span>
            </div>
            {(parties.sellers ?? []).length === 0 ? (
              <p className="text-sm text-gray-500">No seller data yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500 text-left">
                      <th className="pb-2 pr-3">Seller</th>
                      <th className="pb-2 pr-3 text-right">Gross</th>
                      <th className="pb-2 pr-3 text-right">Fee</th>
                      <th className="pb-2 pr-3 text-right">Net</th>
                      <th className="pb-2 text-right">Pending</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    {parties.sellers.slice(0, 15).map((s: any) => (
                      <tr key={s.userId} className="border-t border-gray-700/50">
                        <td className="py-1.5 pr-3">
                          <p className="text-white">{s.name}</p>
                          <p className="text-gray-500 truncate max-w-[160px]">{s.email}</p>
                        </td>
                        <td className="py-1.5 pr-3 font-mono text-right">${s.grossRevenue.toFixed(2)}</td>
                        <td className="py-1.5 pr-3 font-mono text-right text-red-400">${s.platformFees.toFixed(2)}</td>
                        <td className="py-1.5 pr-3 font-mono text-right text-green-400">${s.netEarnings.toFixed(2)}</td>
                        <td className="py-1.5 font-mono text-right text-yellow-400">${s.pendingPayout.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Monthly Revenue History */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-white mb-4">Monthly Revenue History</h3>
        {revenueHistory.length === 0 ? (
          <p className="text-sm text-gray-500">No historical data yet</p>
        ) : (
          <div className="space-y-2">
            {revenueHistory.filter(r => r.gross > 0).map((r: any) => {
              const maxGross = Math.max(...revenueHistory.map((h: any) => h.gross), 1);
              return (
                <div key={r.billingPeriod} className="flex items-center gap-3 text-xs">
                  <span className="text-gray-400 w-20">{r.billingPeriod}</span>
                  <div className="flex-1 h-5 bg-gray-700 rounded overflow-hidden relative">
                    <div
                      className="h-full bg-blue-500/40 rounded"
                      style={{ width: `${(r.gross / maxGross) * 100}%` }}
                    />
                    <div
                      className="absolute top-0 left-0 h-full bg-green-500/60 rounded"
                      style={{ width: `${(r.platformRevenue / maxGross) * 100}%` }}
                    />
                  </div>
                  <div className="text-right w-48">
                    <span className="text-white font-mono">${r.gross.toFixed(2)}</span>
                    <span className="text-green-400 ml-2">(${r.platformRevenue.toFixed(2)})</span>
                    <span className="text-purple-400 ml-2">[${r.sellerEarnings?.toFixed(2) ?? '—'}]</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex gap-4 mt-3 text-[10px] text-gray-500">
          <div className="flex items-center gap-1"><div className="w-3 h-2 bg-blue-500/40 rounded" /> Gross Revenue</div>
          <div className="flex items-center gap-1"><div className="w-3 h-2 bg-green-500/60 rounded" /> Platform Revenue</div>
          <div className="flex items-center gap-1"><span className="text-purple-400">[...]</span> Seller Earnings</div>
        </div>
      </div>

      {/* Seller Payouts Summary */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Wallet className="w-4 h-4 text-gray-400" />
          <h3 className="text-sm font-semibold text-white">Pending Seller Payouts</h3>
        </div>
        <div className="flex items-center gap-6">
          <div>
            <p className="text-xs text-gray-400">Sellers with Pending</p>
            <p className="text-xl font-bold text-white">{overview?.pendingPayouts?.count ?? 0}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Total Pending Amount</p>
            <p className="text-xl font-bold text-yellow-400">${overview?.pendingPayouts?.totalAmount?.toFixed(2) ?? '0.00'}</p>
          </div>
        </div>
      </div>

      {/* Platform Config */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Settings className="w-4 h-4 text-gray-400" />
          <h3 className="text-sm font-semibold text-white">Platform Configuration</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          {Object.entries(overview?.config ?? {}).map(([key, value]) => (
            <div key={key} className="p-3 bg-gray-700/50 rounded-lg">
              <p className="text-gray-400 break-all">{key}</p>
              <p className="text-white font-mono mt-1 break-all">{String(value)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
